import React, { useContext, useEffect, useReducer, useState } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'

const BreadcrumbContent = React.createContext()

export const useBreadcrumbContext = () => useContext(BreadcrumbContent)

export const useRouteStack = () => {
  const history = useHistory()

  const breadcrumbContext = useBreadcrumbContext()

  const backRoute = level => {
    const item = breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]

    if (item) {
      history.replace(item.url)
    } else {
      history.goBack()
    }
  }

  const getStackLevel = level => {
    return breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]
  }

  const replaceStackLevel = (level, append) => {
    const item = breadcrumbContext.stack[breadcrumbContext.stack.length - level - 1]
    if (item) {
      history.replace(`${item.url}/${append}`)
    }
  }

  return { backRoute, getStackLevel, replaceStackLevel }
}

// A crumb's url without its query or hash, and without a trailing slash.
const pathOfCrumb = url => String(url || '').split(/[?#]/)[0].replace(/\/+$/, '')

// Whether a crumb at `url` is above one at `other`: `other` is a path under
// it (/org/sso is above /org/sso/connections/13, not above /org/sso-other).
export const crumbIsAbove = (url, other) => {
  const a = pathOfCrumb(url)
  const b = pathOfCrumb(other)
  return a !== b && b.startsWith(`${a}/`)
}

// Where a view's crumbs (its parents, then its own) go in the trail: in
// front of the first crumb already there that one of them is above, else at
// the end. So the order never depends on which view mounted first: the
// organization's Authentication page swaps one BreadcrumbView for another
// once its plan loads, after a connection's page under it has registered
// its own crumb (audit F1).
export const insertCrumbs = (state, entries) => {
  const at = state.findIndex(item => entries.some(entry => crumbIsAbove(entry.url, item.url)))
  if (at === -1) return [...state, ...entries]
  return [...state.slice(0, at), ...entries, ...state.slice(at)]
}

const useBreadcrumbReducer = () => {
  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'add':
        return insertCrumbs(state, action.payload)
      case 'update':
        let index = state.findIndex(i => i.url === action.payload.url)
        if (index !== -1) {
          let newState = [...state]
          newState[index] = action.payload
          return newState
        } else {
          return state
        }
      case 'remove':
        return state.filter(i => i.url !== action.payload)
      default:
        return state
    }
  }, [])

  const add = (url, text, Icon, block) => dispatch({ type: 'add', payload: [{ url, text, Icon, block }] })
  // Several crumbs that belong together (a view's parents and its own), kept
  // together in the order given.
  const addAll = entries => dispatch({ type: 'add', payload: entries.map(({ url, text, Icon, block }) => ({ url, text, Icon, block })) })
  const update = (url, text, Icon, block) => dispatch({ type: 'update', payload: { url, text, Icon, block } })
  const remove = url => dispatch({ type: 'remove', payload: url })

  return [state, add, update, remove, addAll]
}

const APP_NAME = 'EducationXR'

// item.text is normally a string, but a handful of pages hand it a value that
// isn't ready yet (a record name before its GET resolves is ''/undefined) or,
// in principle, something that isn't a string/number at all -- rendering that
// as a crumb is fine (React just renders it), but stringifying it for
// document.title is not, so anything else is treated as absent rather than
// coerced (which would print "[object Object]" for e.g. a React element).
const breadcrumbTextToString = text => {
  if (typeof text !== 'string' && typeof text !== 'number') {
    return ''
  }

  return String(text).trim()
}

// The crumbs the top bar draws: one whose name is empty or still loading
// ('' or undefined) is left out until it arrives, as the title leaves it out,
// so the trail never shows a bare separator or a lone icon.
export const shownCrumbs = stack => (stack || []).filter(item => {
  if (!item) return false
  if (item.text === undefined || item.text === null) return false
  return typeof item.text !== 'string' || item.text.trim() !== ''
})

// Title = the last two non-empty breadcrumb texts, most specific (deepest,
// i.e. last pushed) first, joined by " · ", then " · EducationXR" -- so every
// history entry / tab / bookmark reads something more useful than the bare
// app name. Exported for unit testing independent of the provider/effect.
export const titleFromStack = stack => {
  const texts = (stack || [])
    .map(item => breadcrumbTextToString(item && item.text))
    .filter(text => text.length > 0)

  const mostSpecificFirst = texts.slice(-2).reverse()

  return [...mostSpecificFirst, APP_NAME].join(' · ')
}

export const BreadcrumbProvider = ({ children }) => {
  const [stack, add, update, remove, addAll] = useBreadcrumbReducer()

  useEffect(
    () => {
      document.title = titleFromStack(stack)
    },
    [stack]
  )

  return (
    <BreadcrumbContent.Provider value={{ stack, add, addAll, update, remove }}>
      {children}
    </BreadcrumbContent.Provider>
  )
}

// The trail is assembled from whichever BreadcrumbViews are MOUNTED, and nesting
// normally falls out of React Router v5's non-exact path matching: at
// /admin/sso/connections/7 the <Route path="/admin/sso/connections"> still
// matches, so the list's BreadcrumbView mounts (registering "PROVIDERS") while
// rendering no children, and the edit page registers itself underneath it.
//
// A page whose path is a SIBLING rather than a descendant -- /admin/sso/jobs
// next to /admin/sso/connections -- has no ancestor route to mount, so it used to
// show a bare one-item trail even though it is only reachable through its
// section. `parent` names that section entry explicitly: it is registered just
// before this view's own, so the trail reads "SSO connections > Jobs" and the
// first crumb navigates back to the section.
//
// `parent` may also be a list, outermost first, for a page reached through a
// longer route than its own path says: the Connect page opened from an
// organization's SSO tab reads "Organizations > <name> > SSO > Connect single
// sign-on" (provider-connect.js). A parent's text may change after mount (a
// name that loads); its crumb is updated in place.
//
// Shape: { url, text, Icon, block } -- the same four values `add` takes.
// `notOver` lists the pages mounted under this view's address that are not
// its pages -- a menu's page that only lives there, such as a course's File
// versions at .../course/:id/edit/publishes, which is the course's, not the
// Edit page's. On those (and anything under them) this view adds no crumb, so
// the trail and the tab title name the page that was opened from (audit
// F16). Each is a path segment under this view's url.
export const BreadcrumbView = ({ children, Icon, text, block, parent, onMatch, onPath, notOver }) => {
  const { url, isExact } = useRouteMatch()

  const [search, updateSearch] = useState()

  const history = useHistory()
  const location = useLocation()

  const breadcrumbContext = useBreadcrumbContext()

  // A parent whose url equals this view's own would duplicate the crumb -- that
  // happens if a section root is handed its own entry as its parent.
  const parents = (Array.isArray(parent) ? parent : [parent]).filter(entry => entry && entry.url && entry.url !== url)
  const parentUrls = parents.map(entry => entry.url).join('|')
  const parentTexts = parents.map(entry => entry.text).join('|')

  const aside = (notOver || []).some(segment => location.pathname === `${url}/${segment}` || location.pathname.startsWith(`${url}/${segment}/`))

  useEffect(
    () => {
      if (aside) return undefined

      breadcrumbContext.addAll([...parents, { url, text, Icon, block }])

      return () => {
        breadcrumbContext.remove(url)
        parents.forEach(entry => breadcrumbContext.remove(entry.url))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, parentUrls]
  )

  useEffect(
    () => {
      breadcrumbContext.update(url, text, Icon, block)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, Icon, block]
  )

  useEffect(
    () => {
      parents.forEach(entry => breadcrumbContext.update(entry.url, entry.text, entry.Icon, entry.block))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parentTexts]
  )

  useEffect(
    () => {
      if (isExact) {
        onMatch && onMatch()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isExact]
  )

  useEffect(
    () => {
      onPath && location.pathname.includes(url) && onPath()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  )

  useEffect(
    () => {
      if (isExact) {
        updateSearch(document.location.search)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document.location.search]
  )

  useEffect(
    () => {
      if (isExact && search) {
        history.replace({ search: search })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isExact]
  )

  return (
    <React.Fragment>
      {isExact &&
        <React.Fragment>
          {children}
        </React.Fragment>}
    </React.Fragment>
  )
}
